import NumberInput from '@/app/components/controls/number-input'
import SelectInput from '@/app/components/controls/select-input'
import HoverableTooltip from '@/app/components/hoverable-tooltip'
import { ModalContext } from '@/app/components/modal'
import InfoIcon from '@/app/icons/info'
import { giphyUrl } from '@/app/util/env'
import Avatar from '@/components/avatar'
import Button, { SubmitButton } from '@/components/button'
import Checkbox from '@/components/checkbox'
import { FlashMessageContext } from '@/context/flash-message'
import UserContext from '@/context/user-context'
import { cn } from '@/util/cn'
import { getCookie } from '@/util/url'
import { giphyDarkCharcoal } from '@giphy/colors'
import { setGADataLayer } from 'analytics'
import { useContext, useEffect, useRef, useState } from 'react'
import { useFormState } from 'react-dom'
import Calendar from 'ui/src/components/controls/calendar'
import { PortalTooltip, useTooltip } from 'ui/src/components/controls/tooltip'
import { getErrorFields } from 'utils/src/api'
import { generateId } from '../channel-overlay'
import { BudgetTooltip, ProjectTooltip, Success } from './tooltips'
import { formatDateObj, getBudget } from './util'

const CalendarComponent = ({
    label,
    onChange,
    min,
}: {
    label: string
    name: string
    min?: Date
    onChange: (date: Date | undefined) => void
}) => {
    const tooltipIdRef = useRef<string>(generateId(label))
    const { isOpen, listeners, closeTooltip } = useTooltip({
        color: giphyDarkCharcoal,
        id: tooltipIdRef.current,
        triggerType: 'click',
        width: 350,
    })
    return (
        <>
            <PortalTooltip isOpen={isOpen}>
                <div className="relative bg-giphyDarkGrey p-4">
                    <Calendar
                        min={min}
                        size="small"
                        onChange={(val) => {
                            closeTooltip()
                            onChange(val)
                        }}
                    />
                </div>
            </PortalTooltip>
            <Button {...listeners} className="bg-gradient-grey">
                {label}
            </Button>
        </>
    )
}
const labelClassName = 'text-giphyWhite font-bold py-2 flex'

const fieldNames = {
    projectType: 'project_type',
    gifCount: 'project_gif_count',
    stickerCount: 'project_sticker_count',
    videoCount: 'project_video_count',
    flexible: 'project_flexible',
    budget: 'budget',
    budget_min: 'project_budget_min',
    budget_max: 'project_budget_max',
    start: 'project_start_date',
    end: 'project_end_date',
    artistId: 'artistId',
}
export const submitForm = async (
    _previousState: string | undefined | null,
    formData: FormData
): Promise<'success' | string> => {
    const [min, max] = getBudget(formData.get(fieldNames.budget)! as string)
    formData.delete(fieldNames.budget)
    const artistId = formData.get(fieldNames.artistId)
    formData.delete(fieldNames.artistId)
    formData.set(fieldNames.budget_min, min)
    formData.set(fieldNames.budget_max, max)
    const payload: Record<string, any> = {}
    formData.forEach((value, key) => (payload[key] = value))
    console.log(`payload`, payload)
    try {
        const response = await fetch(`${giphyUrl}/api/v1/users/${artistId}/freelance-email/`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-CSRFToken': getCookie('csrftoken'),
            },
            body: JSON.stringify(payload),
        })

        if (response.ok) {
            setGADataLayer({
                event: 'hire_form_success',
                options: {
                    form: {
                        formName: 'hire me',
                    },
                },
            })
            return 'success'
        } else {
            const errorFields = getErrorFields(await response.json())
            setGADataLayer({
                event: 'hire_form_error',
                options: {
                    form: {
                        formName: 'hire me',
                        formError: errorFields.join(','),
                    },
                },
            })
        }
    } catch (error) {
        setGADataLayer({
            event: 'hire_form_error',
            options: {
                form: {
                    formName: 'hire me',
                    formError: 'uncaught exception',
                },
            },
        })
    }
    return 'Oops! Something went wrong. Please try again later.'
}

const HireMeForm = () => {
    const { user } = useContext(UserContext)
    const { modal, setModalType } = useContext(ModalContext)
    const [timelineDateStart, setDateStart] = useState<Date | undefined>()
    const [timelineDateEnd, setDateEnd] = useState<Date | undefined>()
    const [result, formAction] = useFormState(submitForm, null)
    const { showMessage } = useContext(FlashMessageContext)
    useEffect(() => {
        let timeout: NodeJS.Timeout
        if (result) {
            if (result !== 'success') {
                showMessage({ message: result, type: 'error' })
            } else {
                // show modal for 3 seconds after success
                timeout = setTimeout(() => {
                    setModalType(null)
                }, 3000)
            }
        }
        return () => clearTimeout(timeout)
    }, [result, showMessage, setModalType])
    useEffect(() => {
        setDateEnd(undefined)
    }, [timelineDateStart])
    if (!user) {
        return null
    }
    if (modal?.name !== 'hireMe') {
        return null
    }
    return result === 'success' ? (
        <Success />
    ) : (
        <form className="group flex flex-col gap-10 p-2" action={formAction}>
            <input hidden type="text" defaultValue={modal.artist.id} name={fieldNames.artistId} />
            <div className="flex gap-2">
                <Avatar user={modal.artist} />
                <div className="flex flex-col justify-center">
                    <h4>Work Inquiry For</h4>
                    <h1>{modal.artist.username}</h1>
                </div>
            </div>
            <div className="flex min-h-fit flex-col gap-6">
                <section>
                    <h3 className={cn(labelClassName, 'items-center gap-3')}>
                        <div className="flex-1">I&apos;m looking for:</div>
                        <div className="text-giphyLightGrey">Uncertain Amount / flexible </div>
                        <Checkbox name="project_flexible" />
                    </h3>
                    <div className="flex justify-between gap-2">
                        <NumberInput label="GIFs" name={fieldNames.gifCount} />
                        <NumberInput label="Animated Stickers" name={fieldNames.stickerCount} />
                        <NumberInput label="Clips (with sound)" name={fieldNames.videoCount} />
                    </div>
                </section>
                <div className="flex justify-between gap-5">
                    <section className="flex-1">
                        <h3 className={cn(labelClassName, 'gap-2')}>
                            What type of project is this?
                            <HoverableTooltip label={<ProjectTooltip />}>
                                <InfoIcon className="size-3" />
                            </HoverableTooltip>
                        </h3>
                        <SelectInput
                            required
                            name={fieldNames.projectType}
                            placeholder="Choose which type(s)"
                            className="w-full"
                            options={[
                                { label: 'Advertising Campaign', value: '' },
                                { label: 'Social Media Campaign', value: '' },
                                { label: 'Editorial', value: '' },
                                { label: 'Personal Promotion', value: '' },
                                { label: 'TV/Broadcast/Film', value: '' },
                                { label: 'Music Video', value: '' },
                                { label: 'Product Design', value: '' },
                                { label: 'Social Good', value: '' },
                            ]}
                        />
                    </section>
                    <section className="flex-1">
                        <h3 className={cn(labelClassName, 'gap-2')}>
                            Project Budget
                            <HoverableTooltip label={<BudgetTooltip />}>
                                <InfoIcon className="size-3" />
                            </HoverableTooltip>
                        </h3>
                        <SelectInput
                            required
                            className="w-full"
                            placeholder="Select Budget"
                            name={fieldNames.budget}
                            options={[
                                { label: '$500 - $2,000', value: '' },
                                { label: '$2,000 - $5,000', value: '' },
                                { label: '$5,000 - $10,000', value: '' },
                                { label: '$10,000 - $25,000', value: '' },
                                { label: '$25,000 - $50,000', value: '' },
                                { label: '+$50,000', value: '' },
                            ]}
                        />
                    </section>
                </div>
                <section className="">
                    <h3 className={labelClassName}>Project Timeline</h3>
                    <div className="flex gap-3">
                        <div className="flex items-center gap-3">
                            <div>Start:</div>
                            <input
                                type="text"
                                hidden
                                required
                                defaultValue={timelineDateStart ? formatDateObj(timelineDateStart) : ''}
                                name={fieldNames.start}
                            />
                            <CalendarComponent
                                min={new Date()}
                                name={fieldNames.start}
                                label={timelineDateStart?.toLocaleDateString() || 'Select Date Start'}
                                onChange={setDateStart}
                            />
                        </div>
                        <div className="flex items-center gap-3">
                            <div>End:</div>
                            <input
                                type="text"
                                hidden
                                required
                                defaultValue={timelineDateEnd ? formatDateObj(timelineDateEnd) : ''}
                                name={fieldNames.end}
                            />
                            <CalendarComponent
                                name={fieldNames.end}
                                min={timelineDateStart}
                                label={timelineDateEnd?.toLocaleDateString() || 'Select Date End'}
                                onChange={setDateEnd}
                            />
                        </div>
                    </div>
                </section>
                <section className="">
                    <h3 className={cn(labelClassName, 'flex justify-between')}>
                        <div>Contact Email</div>
                        <div className="text-giphyLightGrey">Any responses will be sent here</div>
                    </h3>
                    <input
                        type="email"
                        required
                        name="email"
                        defaultValue={user.email}
                        className="w-full bg-giphyDarkGrey p-2 text-white"
                    />
                </section>
            </div>
            <div className="flex flex-col gap-2">
                <SubmitButton
                    className={cn(
                        'w-full group-invalid:pointer-events-none group-invalid:bg-gradient-grey group-invalid:opacity-50'
                    )}
                >
                    Send Inquiry
                </SubmitButton>
                <h5 className="text-center">
                    Clicking “Send Inquiry” will send an email to this Artist containing the information you entered,
                    including your email address. Please ensure you are comfortable sharing the information entered.
                </h5>
            </div>
        </form>
    )
}

export default HireMeForm
